import { useSyncExternalStore } from 'react';
import { perceptualGain, type AudioBus, type Lane } from './audioBus';
import { RADIO_STATIONS, type RadioStation } from './radioStations';

export type RadioStatus = 'off' | 'idle' | 'connecting' | 'playing' | 'blocked' | 'lost' | 'asleep';

/**
 * The music's own level on its lane, over the Music slider's curve. It was 0.9, a little under the
 * game's sounds; the owner asked for the music about 66 % louder at every slider position, the
 * slider keeping its 0.6 (2026-09-26, D-52). Past 1 the lane lifts the stream above its own level,
 * and the bus's limiter keeps the mix from clipping. A plain element cannot play past its own full
 * volume, so there it stops at 1.
 */
export const RADIO_GAIN = 0.9 * 1.66;

/** Where the Music slider stands until a player moves it. */
export const MUSIC_VOLUME_DEFAULT = 0.6;

/**
 * Whether a station is on: tuning in, playing, or lost and trying again. A lamp remembered but not
 * pressed, one the browser held back and one asleep play nothing, and the ambience plays in their
 * place (the owner, 2026-09-26).
 */
export function stationOn(state: Pick<RadioState, 'status'>): boolean {
  return state.status === 'connecting' || state.status === 'playing' || state.status === 'lost';
}

interface StationHealth {
  failures: number;
  demotedAt?: number;
  retryAt?: number;
  retryMs?: number;
}

interface StoredRadio {
  selected: string | null;
  volume: number;
  health: Record<string, StationHealth>;
}

export interface RadioState extends StoredRadio {
  status: RadioStatus;
  playing: string | null;
}

interface RadioStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const STORAGE_KEY = 'archon3d.radio';
const WATCHDOG_MS = 12_000;
const STILL_LISTENING_MS = 4 * 60 * 60 * 1000;
const FIRST_HEALTH_RETRY_MS = 30 * 60 * 1000;
const MAX_HEALTH_RETRY_MS = 24 * 60 * 60 * 1000;
const RECONNECT_MS = [5_000, 15_000, 30_000, 60_000] as const;
const GESTURES = ['pointerdown', 'keydown'] as const;

const clamp01 = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;

function browserStorage(): RadioStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function load(storage: RadioStorage | null): StoredRadio {
  const fallback: StoredRadio = { selected: null, volume: MUSIC_VOLUME_DEFAULT, health: {} };
  try {
    const parsed = JSON.parse(
      storage?.getItem(STORAGE_KEY) ?? 'null',
    ) as Partial<StoredRadio> | null;
    if (!parsed) return fallback;
    const selected = RADIO_STATIONS.some((station) => station.id === parsed.selected)
      ? (parsed.selected as string)
      : null;
    return {
      selected,
      volume: clamp01(parsed.volume, fallback.volume),
      health: parsed.health && typeof parsed.health === 'object' ? parsed.health : {},
    };
  } catch {
    return fallback;
  }
}

/** One local, direct-to-broadcaster radio. Nothing here is shared with a multiplayer room. */
export class RadioPlayer {
  private readonly listeners = new Set<() => void>();
  private state: RadioState;
  private wired: HTMLAudioElement | null = null;
  private plain: HTMLAudioElement | null = null;
  private current: HTMLAudioElement | null = null;
  private lane: Lane | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private reconnect: ReturnType<typeof setTimeout> | null = null;
  private retryGesture: (() => void) | null = null;
  /** The tab was hidden while the radio was on: only then does showing it start a stream. */
  private resumeOnShow = false;
  private generation = 0;
  private reconnectRound = 0;
  private lastInteraction = Date.now();
  private masterVolume = 1;
  private masterMuted = false;

  constructor(
    private readonly bus: AudioBus,
    private readonly storage: RadioStorage | null = browserStorage(),
  ) {
    const stored = load(storage);
    // Remember the lamp and volume but never start a stream on page load.
    this.state = { ...stored, status: stored.selected ? 'idle' : 'off', playing: null };
  }

  /**
   * Follows the tab hiding and showing, and the player at the page, until the returned function
   * is called. Not in the constructor: a player React builds twice in development and throws
   * one away must listen to nothing (client-audio-engine:quality#2).
   */
  attach(): () => void {
    if (typeof document === 'undefined') return () => {};
    const touch = () => this.touch();
    document.addEventListener('visibilitychange', this.visibility);
    for (const event of GESTURES) window.addEventListener(event, touch, { capture: true });
    const sleep = setInterval(this.checkSleep, 30_000);
    return () => {
      document.removeEventListener('visibilitychange', this.visibility);
      for (const event of GESTURES) window.removeEventListener(event, touch, { capture: true });
      clearInterval(sleep);
    };
  }

  /**
   * Someone is at the page or playing: the four-hour sleep counts from here, so the radio no
   * longer stops in the middle of a long evening's play (client-audio-engine:quality#11).
   */
  touch(): void {
    this.lastInteraction = Date.now();
  }

  get(): RadioState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  isOutOfOrder(id: string, now = Date.now()): boolean {
    const health = this.state.health[id];
    return Boolean(health?.demotedAt && (health.retryAt ?? 0) > now);
  }

  async select(id: string | null): Promise<void> {
    this.touch();
    if (id && !RADIO_STATIONS.some((station) => station.id === id)) return;
    if (id && this.isOutOfOrder(id)) return;
    this.resumeOnShow = false;
    this.patch({ selected: id, status: id ? 'connecting' : 'off', playing: null });
    this.persist();
    this.reconnectRound = 0;
    if (!id) {
      this.stopElements();
      return;
    }
    await this.start(id);
  }

  setVolume(volume: number): void {
    this.touch();
    this.patch({ volume: clamp01(volume, this.state.volume) });
    this.applyVolume();
    this.persist();
  }

  /** The plain-element fallback cannot enter Web Audio, so mirror the game's master onto it. */
  setMaster(volume: number, muted: boolean): void {
    this.masterVolume = clamp01(volume, 1);
    this.masterMuted = muted;
    this.applyVolume();
  }

  dispose(): void {
    this.stopElements();
    this.resumeOnShow = false;
    this.lane?.dispose();
    this.lane = null;
    this.source = null;
    this.disarmGesture();
    this.listeners.clear();
  }

  /**
   * A radio that is on stops with the tab and starts again when it is shown. One that is not
   * stays silent: before, a lamp remembered from another visit started its stream at the
   * first tab switch or headset doff (client-audio-engine:quality#2, client-audio-engine:bugs#7).
   */
  private readonly visibility = () => {
    if (document.hidden) {
      const { status } = this.state;
      const on =
        status === 'playing' ||
        status === 'connecting' ||
        (status === 'lost' && this.reconnect !== null);
      if (on) {
        this.stopElements();
        this.resumeOnShow = true;
        this.patch({ status: 'idle', playing: null });
      }
    } else if (this.resumeOnShow) {
      this.resumeOnShow = false;
      if (this.state.selected) void this.start(this.state.selected);
    }
  };

  private readonly checkSleep = () => {
    if (
      this.state.status === 'playing' &&
      Date.now() - this.lastInteraction >= STILL_LISTENING_MS
    ) {
      this.stopElements();
      this.patch({ status: 'asleep', playing: null });
    }
  };

  private element(kind: 'wired' | 'plain'): HTMLAudioElement {
    const existing = kind === 'wired' ? this.wired : this.plain;
    if (existing) return existing;
    const el = document.createElement('audio');
    el.preload = 'none';
    el.setAttribute('playsinline', '');
    if (kind === 'wired') {
      el.crossOrigin = 'anonymous';
      this.wired = el;
    } else this.plain = el;
    return el;
  }

  private async ensureGraph(): Promise<boolean> {
    if (this.lane?.alive && this.source) return true;
    this.lane?.dispose();
    this.lane = await this.bus.mediaLane();
    if (!this.lane) return false;
    try {
      this.source = (this.lane.context as AudioContext).createMediaElementSource(
        this.element('wired'),
      );
      this.source.connect(this.lane.input);
      this.applyVolume(true);
      return true;
    } catch {
      this.lane.dispose();
      this.lane = null;
      this.source = null;
      return false;
    }
  }

  private async start(id: string): Promise<void> {
    const station = RADIO_STATIONS.find((candidate) => candidate.id === id);
    if (!station) return;
    if (document.hidden) {
      // Chosen while hidden: it plays when the tab is shown, and says so meanwhile rather than
      // tuning in forever.
      this.resumeOnShow = true;
      this.patch({ status: 'idle', playing: null });
      return;
    }
    this.stopElements();
    const generation = this.generation;
    this.patch({ status: 'connecting', playing: null });
    const wiredReady = await this.ensureGraph();
    if (generation !== this.generation || this.state.selected !== id) return;
    const wiredWorked =
      wiredReady && (await this.tryElement(this.element('wired'), station, generation));
    if (wiredWorked || generation !== this.generation || this.state.selected !== id) return;
    const plainWorked = await this.tryElement(this.element('plain'), station, generation);
    if (plainWorked || generation !== this.generation || this.state.selected !== id) return;
    this.recordFailure(id);
    this.patch({ status: 'lost', playing: null });
    if (!this.isOutOfOrder(id)) this.scheduleReconnect(id, generation);
  }

  private tryElement(
    el: HTMLAudioElement,
    station: RadioStation,
    generation: number,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      let done = false;
      const finish = (ok: boolean) => {
        if (done) return;
        done = true;
        if (this.watchdog) clearTimeout(this.watchdog);
        this.watchdog = null;
        el.onplaying = el.onerror = el.onstalled = el.onwaiting = el.onended = null;
        if (!ok) {
          el.pause();
          el.removeAttribute('src');
          el.load();
        }
        resolve(ok);
      };
      el.onplaying = () => {
        if (generation !== this.generation) return finish(false);
        if (this.watchdog) clearTimeout(this.watchdog);
        this.watchdog = null;
        this.current = el;
        this.clearHealth(station.id);
        this.reconnectRound = 0;
        this.patch({ status: 'playing', playing: station.id });
        const loseSignal = () => {
          if (generation !== this.generation || this.state.selected !== station.id) return;
          this.recordFailure(station.id);
          this.stopElements();
          this.patch({ status: 'lost', playing: null });
          if (!this.isOutOfOrder(station.id)) this.scheduleReconnect(station.id, this.generation);
        };
        el.onstalled = () => {
          if (this.watchdog) clearTimeout(this.watchdog);
          this.watchdog = setTimeout(() => {
            if (el.readyState < 3) loseSignal();
          }, WATCHDOG_MS);
        };
        el.onwaiting = el.onstalled;
        el.onended = loseSignal;
        el.onerror = loseSignal;
        el.onplaying = () => {
          if (this.watchdog) clearTimeout(this.watchdog);
          this.watchdog = null;
        };
        done = true;
        resolve(true);
      };
      el.onerror = () => finish(false);
      el.src = station.url;
      el.load();
      this.applyVolume();
      this.watchdog = setTimeout(() => finish(false), WATCHDOG_MS);
      void el.play().catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          if (this.watchdog) clearTimeout(this.watchdog);
          this.watchdog = null;
          this.patch({ status: 'blocked', playing: null });
          this.armGesture(station.id, generation);
          return finish(true);
        }
        finish(false);
      });
    });
  }

  private scheduleReconnect(id: string, generation: number): void {
    if (this.reconnect) clearTimeout(this.reconnect);
    const delay = RECONNECT_MS[Math.min(this.reconnectRound, RECONNECT_MS.length - 1)]!;
    this.reconnectRound += 1;
    this.reconnect = setTimeout(() => {
      if (generation === this.generation && this.state.selected === id) void this.start(id);
    }, delay);
  }

  private recordFailure(id: string): void {
    const previous = this.state.health[id] ?? { failures: 0 };
    const failures = previous.failures + 1;
    const now = Date.now();
    const retryMs =
      failures >= 3
        ? Math.min(
            MAX_HEALTH_RETRY_MS,
            previous.demotedAt
              ? (previous.retryMs ?? FIRST_HEALTH_RETRY_MS) * 2
              : FIRST_HEALTH_RETRY_MS,
          )
        : previous.retryMs;
    const health = {
      ...this.state.health,
      [id]: {
        failures,
        ...(failures >= 3 ? { demotedAt: now, retryAt: now + retryMs!, retryMs } : {}),
      },
    };
    this.patch({ health });
    this.persist();
  }

  private clearHealth(id: string): void {
    if (!this.state.health[id]) return;
    const health = { ...this.state.health };
    delete health[id];
    this.patch({ health });
    this.persist();
  }

  private armGesture(id: string, generation: number): void {
    this.disarmGesture();
    const retry = () => {
      this.disarmGesture();
      if (generation === this.generation && this.state.selected === id) void this.start(id);
    };
    this.retryGesture = retry;
    for (const event of GESTURES) window.addEventListener(event, retry, { once: true });
  }

  private disarmGesture(): void {
    if (!this.retryGesture) return;
    for (const event of GESTURES) window.removeEventListener(event, this.retryGesture);
    this.retryGesture = null;
  }

  private stopElements(): void {
    this.generation += 1;
    if (this.watchdog) clearTimeout(this.watchdog);
    if (this.reconnect) clearTimeout(this.reconnect);
    this.watchdog = this.reconnect = null;
    this.disarmGesture();
    for (const el of [this.wired, this.plain]) {
      if (!el) continue;
      el.onplaying = el.onerror = el.onstalled = el.onwaiting = el.onended = null;
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    this.current = null;
  }

  /** The radio at `RADIO_GAIN`, on the same curve as the sound slider. */
  private applyVolume(fresh = false): void {
    const gain = perceptualGain(this.state.volume) * RADIO_GAIN;
    // Eased as the master is, so a drag of the slider does not step a stream's gain (a zipper);
    // a lane just made starts where it should.
    const lane = this.lane?.input.gain;
    if (lane && fresh) lane.value = gain;
    else lane?.setTargetAtTime(gain, this.lane!.context.currentTime, 0.03);
    if (this.plain)
      this.plain.volume = this.masterMuted
        ? 0
        : Math.min(1, gain * perceptualGain(this.masterVolume));
  }

  private patch(patch: Partial<RadioState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private persist(): void {
    try {
      this.storage?.setItem(
        STORAGE_KEY,
        JSON.stringify({
          selected: this.state.selected,
          volume: this.state.volume,
          health: this.state.health,
        } satisfies StoredRadio),
      );
    } catch {
      // The radio keeps working for this visit when storage is blocked or full.
    }
  }
}

export function useRadio(player: RadioPlayer): RadioState {
  return useSyncExternalStore(
    (listener) => player.subscribe(listener),
    () => player.get(),
    () => player.get(),
  );
}
